import { ComponentMeta, Story } from "@storybook/react";
import React from "react";
import { useTheme } from "styled-components";
import { Flex, Table, Typography } from "../components";
import { emToPx } from "../helpers";
import { TTypographyVariant } from "../types";

type TTypographyValue = {
    id: number;
    variant: TTypographyVariant;
};

interface TGroupedLabel {
    label: string;
    values: Array<TTypographyValue>;
}

const getGroupedSizes = (typography: TTypographyVariant[]): TGroupedLabel[] => {
    const result: TGroupedLabel[] = [];

    const sortedTypography = typography.reverse();

    sortedTypography.forEach(size => {
        const groupName: string = size.match(/^([\w-]+)-\w+$/)?.[1] ?? "unknown";

        const foundIndex = result.findIndex(group => group.label === groupName);

        if (foundIndex > -1) {
            result[foundIndex].values.push({
                id: result[foundIndex].values.length,
                variant: size
            });
            return;
        }

        result.push({
            label: groupName,
            values: [
                {
                    id: 0,
                    variant: size
                }
            ]
        });
    });

    return result;
};

export default {
    title: "Foundation/Typography Variants"
} as ComponentMeta<typeof Typography>;

const Data: React.FC<{ title: string }> = ({ title, children }) => {
    return (
        <Flex column spacing={0.5}>
            <Typography variant="paragraph-x-small-regular" color="text-primary-600">
                {title}
            </Typography>
            <Typography variant="paragraph-small-regular" color="text-primary-900">
                {children}
            </Typography>
        </Flex>
    );
};

export const Default: Story = () => {
    const {
        v2: { typography }
    } = useTheme();

    const typographyKeys = React.useMemo<TGroupedLabel[]>(
        () => getGroupedSizes(Object.keys(typography) as TTypographyVariant[]),
        [typography]
    );

    return (
        <Flex column spacing={4}>
            <Flex column spacing={0.5}>
                <Typography variant="label-large-semibold" color="text-primary-1200">
                    Typography variants
                </Typography>
                <Typography variant="paragraph-regular-regular" color="text-primary-900">
                    Below you can find all available typography variants. We use weight, size, and color to create
                    hierarchy of our content, with that users have a better idea of what is more or less important
                    within the interface. Clicking on a size will copy a full key for you, so you can quickly paste it
                    into your code.
                </Typography>
            </Flex>
            {typographyKeys.map(size => {
                const sizeKey = `${size.label}-regular` as TTypographyVariant;

                return (
                    <Table
                        key={size.label}
                        header={
                            <Flex column spacing={2}>
                                <Typography variant="label-regular-semibold" color="text-primary-1200">
                                    {size.label}
                                </Typography>
                                <Flex spacing={2}>
                                    <Data title="Font Size">{`${emToPx(typography[sizeKey].fontSize)}px`}</Data>
                                    <Data title="Letter Spacing">{`${emToPx(
                                        typography[sizeKey].letterSpacing
                                    )}px`}</Data>
                                </Flex>
                            </Flex>
                        }
                        items={size.values}
                        columnDefinitions={[
                            {
                                id: "size",
                                header: "Size",
                                size: 270,
                                cell: cell => cell.row.original.variant
                            },
                            {
                                id: "weight",
                                header: "Font Weight",
                                size: 150,
                                cell: cell => typography[cell.row.original.variant].fontWeight
                            },
                            {
                                id: "result",
                                header: "Result",
                                cell: cell => (
                                    <Typography
                                        variant={cell.row.original.variant}
                                        color="text-primary-1200"
                                        styles={{
                                            maxWidth: "600px",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap"
                                        }}
                                    >
                                        Example text
                                    </Typography>
                                )
                            }
                        ]}
                        disableColumnAutoSizing
                    />
                );
            })}
        </Flex>
    );
};
